import { useEffect, useState, useMemo, useRef } from "react";

import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  HStack,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
} from "@chakra-ui/react";
import { Search, Package, Download, Filter } from "lucide-react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAccessCheck } from "@/hooks/useAccess";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import MobileProductCard from "@/components/pageBuilder/MobileProductCard";
import AddProductModal from "@/components/modals/AddProductModal";
import ReplenishModal from "@/components/modals/ReplenishModal";
import PharmacyStatsCard from "@/components/StatsCard/PharmacyStatsCard";
import { DataTable, type DataTableRef } from "@/components/tables/DataTable";
import type { DataTableOptions, Permission } from "@/components/tables/types";
import { getStatusColorAndBg } from "@/util/getStatusColor";

import {
  productService,
  type Product,
  type ProductStats,
} from "@/lib/services/productService";

// Add this type for the API response
interface ProductApiResponse {
  success?: boolean;
  data: Product[];
  meta?: any;
}

export default function PharmacyPage() {
  const { showError, showSuccess } = useToastNotification();
  const access = useAccessCheck();
  const dataTableRef = useRef<DataTableRef>(null);

  // State
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [stats, setStats] = useState<ProductStats | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isReplenishModalOpen, setIsReplenishModalOpen] = useState(false);
  const [total, setTotal] = useState(0);

  // Permissions
  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
    }),
    [access],
  );

  // Get unique categories
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(allProducts.map((p) => p.category)),
    ).filter(Boolean);
    return uniqueCategories;
  }, [allProducts]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const data = await productService.getProductStats();
      setStats(data);
    } catch (error: any) {
      console.error("Error fetching stats:", error);
    }
  };

  // Fetch products manually for mobile view
  const fetchProductsForMobile = async () => {
    try {
      const filters: any = {};
      if (searchTerm) filters.search = searchTerm;
      if (statusFilter) filters.status = statusFilter;
      if (categoryFilter) filters.category = categoryFilter;

      const response = (await productService.getProducts(filters)) as any;

      let fetchedProducts: Product[] = [];

      // Handle the response with 'success' property
      if (response?.success === true && Array.isArray(response?.data)) {
        fetchedProducts = response.data;
      } else if (response?.data && Array.isArray(response.data)) {
        fetchedProducts = response.data;
      } else if (Array.isArray(response)) {
        fetchedProducts = response;
      } else {
        fetchedProducts = [];
      }

      setAllProducts(fetchedProducts);
    } catch (error: any) {
      console.error("Error fetching products:", error);
    }
  };

  // CSV Export Function
  const handleCSVExport = async () => {
    setIsExporting(true);
    try {
      const filters: any = {};
      if (searchTerm) filters.search = searchTerm;
      if (statusFilter) filters.status = statusFilter;
      if (categoryFilter) filters.category = categoryFilter;

      const response = (await productService.getProducts(filters)) as any;

      let productsToExport: Product[] = [];

      // Handle the response with 'success' property
      if (response?.success === true && Array.isArray(response?.data)) {
        productsToExport = response.data;
      } else if (response?.data && Array.isArray(response.data)) {
        productsToExport = response.data;
      } else if (Array.isArray(response)) {
        productsToExport = response;
      } else {
        productsToExport = [];
      }

      if (productsToExport.length === 0) {
        showError("No products to export");
        return;
      }

      const headers = [
        "ID",
        "Serial No",
        "Lot No",
        "Batch No",
        "Product Name",
        "Category",
        "Quantity",
        "Threshold",
        "Expiration Date",
        "Status",
        "Type",
        "Created At",
        "Updated At",
      ];

      const csvRows = [
        headers.join(","),
        ...productsToExport.map((product) =>
          [
            product.id || "",
            `"${product.serial_no || ""}"`,
            `"${product.lot_no || ""}"`,
            product.batch_no || "",
            `"${product.product_name || ""}"`,
            `"${product.category || ""}"`,
            product.quantity || 0,
            product.threshold || 0,
            product.expiration_date || "",
            `"${product.status || ""}"`,
            `"${product.type || ""}"`,
            product.created_at || "",
            product.updated_at || "",
          ].join(","),
        ),
      ];

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `products_export_${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSuccess("Success", "Products exported successfully as CSV");
    } catch (error: any) {
      showError(error.message || "Failed to export products");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveProduct = async (productData: {
    serialNumber: string;
    productName: string;
    initialQuantity: number;
    category: string;
    lotNumber: string;
    expirationDate: string;
    threshold: number;
    type: string;
  }) => {
    try {
      const createData = {
        product_name: productData.productName,
        quantity: productData.initialQuantity,
        expiration_date: productData.expirationDate,
        category: productData.category,
        threshold: productData.threshold,
        type: productData.type,
        serial_no: productData.serialNumber || undefined,
        lot_no: productData.lotNumber || undefined,
      };

      await productService.createProduct(createData);
      showSuccess("Success", "Product added successfully");
      setIsAddProductModalOpen(false);
      if (dataTableRef.current) {
        dataTableRef.current.reload();
      }
      fetchStats();
      fetchProductsForMobile();
    } catch (error: any) {
      showError(error.message || "Failed to add product");
    }
  };

  // DataTable options
  const dataTableOptions: DataTableOptions = useMemo(
    () => ({
      dataSource: "/products",
      primaryKey: "id",
      dataFields: {
        serial_no: {
          name: "Serial No.",
        },
        lot_no: {
          name: "Lot No.",
        },
        batch_no: {
          name: "Batch No.",
        },
        product_name: {
          name: "Product Name",
        },

        quantity: {
          name: "Quantity",
        },
        expiration_date: {
          name: "Expiration Date",
        },
        status: {
          name: "Status",
        },
      },
      noRecordText: "No products found",
      displayInModal: false,
      softDelete: false,
    }),
    [],
  );

  // Custom renderer for status badge - UPDATED to match the working implementation
  const customRenderers = useMemo(
    () => ({
      status: (value: string) => {
        const { color, bg } = getStatusColorAndBg(value);

        // Create a color scheme mapping
        const getColorScheme = (color: string) => {
          switch (color) {
            case "green":
              return "green";
            case "red":
              return "red";
            case "yellow":
              return "yellow";
            default:
              return "gray";
          }
        };

        const getBorderColor = (color: string) => {
          switch (color) {
            case "green":
              return "green.200";
            case "red":
              return "red.200";
            case "yellow":
              return "yellow.200";
            default:
              return "gray.200";
          }
        };

        const colorScheme = getColorScheme(color);
        const borderColor = getBorderColor(color);

        return (
          <Badge
            colorScheme={colorScheme}
            bg={bg}
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="md"
            variant="outline"
            border="1px"
            borderColor={borderColor}
            fontWeight="medium"
          >
            {value}
          </Badge>
        );
      },
      product_name: (value: string, row: any) => (
        <Box>
          <Text fontSize="sm" fontWeight="400">
            {value}
          </Text>
          <Text fontSize="xs" color="gray.600">
            {row?.category}
          </Text>
        </Box>
      ),
    }),
    [],
  );

  // Params for DataTable
  const dataTableParams = useMemo(() => {
    const params: any = {};
    if (searchTerm) params.search = searchTerm;
    if (statusFilter) params.status = statusFilter;
    if (categoryFilter) params.category = categoryFilter;
    return params;
  }, [searchTerm, statusFilter, categoryFilter]);

  // Handle filter changes
  useEffect(() => {
    if (dataTableRef.current) {
      dataTableRef.current.clearPage();
    }
  }, [searchTerm, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchProductsForMobile();
  }, [searchTerm, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchStats();
    fetchProductsForMobile();
  }, []);

  return (
    <AdminLayout>
      <Box
        minH="calc(100vh - 110px)"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        bg="#FEFCF8"
        py={1}
        px={5}
      >
        <Card flexShrink={0} bg="transparent" boxShadow="none" border="none">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Quick Stats
            </Heading>
          </CardHeader>

          <CardBody pb={4} px={5}>
            {stats && (
              <PharmacyStatsCard
                stats={{
                  total_products: stats.total_products || 0,
                  expired_products: stats.expired_products || 0,
                  low_stock_products: stats.low_stock_products || 0,
                }}
              />
            )}
          </CardBody>

          <CardHeader flexShrink={0} py={1}>
            <Flex
              direction={{ base: "column", lg: "row" }}
              justify="space-between"
              align={{ base: "flex-start", lg: "center" }}
              gap={{ base: 3, lg: 0 }}
            >
              <Heading size="md" mb={1}>
                Products {total > 0 && `(${total})`}
              </Heading>
              <HStack spacing={3} flexWrap="wrap">
                <Button
                  onClick={handleCSVExport}
                  variant="outline"
                  size={{ base: "sm", lg: "sm" }}
                  fontSize={{ base: "xs", lg: "sm" }}
                  isDisabled={!permissions.can_view || isExporting}
                  bg="white"
                  borderColor="gray.200"
                  _hover={{ borderColor: "gray.300" }}
                  _active={{ bg: "gray.50" }}
                  fontWeight="500"
                  px={4}
                  isLoading={isExporting}
                  loadingText="Exporting..."
                  leftIcon={<Download size={16} />}
                >
                  Export
                </Button>

                {permissions.can_create && (
                  <Button
                    onClick={() => setIsAddProductModalOpen(true)}
                    variant="outline"
                    bg="#ffffff"
                    borderColor="gray.200"
                    size={{ base: "sm", md: "sm" }}
                    fontSize={{ base: "xs", md: "sm" }}
                    _hover={{ borderColor: "gray.300" }}
                    _active={{ bg: "gray.50" }}
                  >
                    + New Product
                  </Button>
                )}

                <Button
                  onClick={() => setIsReplenishModalOpen(true)}
                  bg="#DC3611"
                  _hover={{ bg: "#bf3c1fff" }}
                  color="white"
                  size={{ base: "sm", md: "sm" }}
                  fontSize={{ base: "xs", md: "sm" }}
                  leftIcon={<Package size={16} />}
                >
                  Replenish Stock
                </Button>
              </HStack>
            </Flex>
          </CardHeader>
          <Box
            borderBottom="1px solid #E7E8EB"
            pb={5}
            mb={4}
            ml={5}
            width="97%"
          ></Box>
          <CardHeader
            py={1}
            pt={2}
            pb={2}
            flexShrink={0}
            display="flex"
            justifyContent="space-between"
            flexDirection={{ base: "column", lg: "row" }}
          >
            <HStack spacing={4} flexWrap="wrap" alignItems={"center"}>
              <Box
                flex="0 1 auto"
                minW="250px"
                maxW="300px"
                h="32px"
                bg="white"
              >
                <InputGroup size="lg" h="full">
                  <InputLeftElement pointerEvents="none" h="full">
                    <Search size={16} color="#9DA4B0" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fontSize="14px"
                    h="full"
                  />
                </InputGroup>
              </Box>
            </HStack>
            <HStack
              spacing={4}
              flexWrap="wrap"
              alignItems={"center"}
              mt={{ base: 3, lg: 0 }}
            >
              <Menu>
                <MenuButton
                  as={Button}
                  variant="outline"
                  size={{ base: "sm", lg: "sm" }}
                  fontSize={{ base: "xs", lg: "sm" }}
                  leftIcon={<Filter size={16} />}
                  isDisabled={!permissions.can_view}
                  bg="white"
                >
                  Filter: {statusFilter || "All Status"}
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => setStatusFilter(null)}>
                    All Status
                  </MenuItem>
                  <MenuItem onClick={() => setStatusFilter("Good")}>
                    Good
                  </MenuItem>
                  <MenuItem onClick={() => setStatusFilter("Expired")}>
                    Expired
                  </MenuItem>
                  <MenuItem
                    onClick={() => setStatusFilter("Need to replenish")}
                  >
                    Need to Replenish
                  </MenuItem>
                </MenuList>
              </Menu>
              {categories.length > 0 && (
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="outline"
                    size={{ base: "sm", lg: "sm" }}
                    fontSize={{ base: "xs", lg: "sm" }}
                    leftIcon={<Filter size={16} />}
                    isDisabled={!permissions.can_view}
                    bg="white"
                  >
                    Category: {categoryFilter || "All"}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setCategoryFilter(null)}>
                      All Categories
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem
                        key={category}
                        onClick={() => setCategoryFilter(category)}
                      >
                        {category}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </HStack>
          </CardHeader>
          <CardBody
            p={0}
            pt={1}
            pb={4}
            mx={5}
            flex="1"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
          >
            {/* For mobile view*/}
            <Box
              display={{ base: "block", lg: "none" }}
              overflowY="auto"
              flex="1"
              px={2}
            >
              {allProducts.length > 0 ? (
                allProducts.map((product) => (
                  <MobileProductCard
                    key={product.id}
                    product={product}
                    permissions={permissions}
                    onReplenish={() => setIsReplenishModalOpen(true)}
                  />
                ))
              ) : (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500">No products found</Text>
                </Box>
              )}
            </Box>

            {/* For desktop view*/}
            <Box
              display={{ base: "none", lg: "flex" }}
              flexDirection="column"
              h="100%"
              minH={0}
              overflow="hidden"
            >
              <DataTable
                ref={dataTableRef}
                options={dataTableOptions}
                params={dataTableParams}
                access={permissions as Permission}
                showCheckbox={false}
                editable={false}
                itemClickable={false}
                setTotal={setTotal}
                customRenderers={customRenderers}
                onOpenItem={(id) => {
                  console.log("Open product:", id);
                }}
              />
            </Box>
          </CardBody>
        </Card>
      </Box>

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={isAddProductModalOpen}
        onClose={() => setIsAddProductModalOpen(false)}
        onSave={handleSaveProduct}
        categories={[
          "Medication",
          "Surgery",
          "Pain Management",
          "Infection Control",
          "Inflammation & Healing",
          "Anesthesia & Sedation",
          "Diagnostic & Laboratory",
          "Emergency & Trauma Care",
          "Vitamins & Supplements",
          "Medical Supplies",
          "Patient Support",
        ]}
        productTypes={[
          "Tablet",
          "Capsule",
          "Syrup",
          "Injection",
          "Suspension",
          "Solution",
          "Drops",
          "Ointment",
          "Cream",
          "Gel",
          "Spray",
          "Inhaler",
          "Patch",
          "Powder",
          "Liquid",
          "IV Fluid",
          "Device",
          "Suture",
          "Disposable",
        ]}
      />

      {/* Replenish Modal */}
      <ReplenishModal
        isOpen={isReplenishModalOpen}
        onClose={() => setIsReplenishModalOpen(false)}
        onSuccess={() => {
          if (dataTableRef.current) {
            dataTableRef.current.reload();
          }
          fetchStats();
          fetchProductsForMobile();
        }}
        products={allProducts}
      />
    </AdminLayout>
  );
}
